import type { Map as MapboxMap } from 'mapbox-gl';
import { hideMap3d, showMap3d } from './basemap';

const PITCH_MS = 600;

function pitchDuration(): number {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return PITCH_MS;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : PITCH_MS;
}

/**
 * A "3D" button inside the zoom/compass group. The map stays flat until it is pressed.
 */
export class Map3dToggleControl {
  private button: HTMLButtonElement | null = null;
  private placeholder: HTMLElement | null = null;
  private map: MapboxMap | null = null;
  private enabled = false;

  onAdd(map: MapboxMap): HTMLElement {
    this.map = map;
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'mapboxgl-ctrl-3d';
    this.setLabel(button, false);

    const icon = document.createElement('span');
    icon.className = 'mapboxgl-ctrl-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '3D';
    button.appendChild(icon);
    button.addEventListener('click', this.toggle);
    this.button = button;

    const group = map.getContainer().querySelector('.mapboxgl-ctrl-zoom-out')?.parentElement;
    if (group) {
      group.appendChild(button);
      const placeholder = document.createElement('span');
      placeholder.hidden = true;
      this.placeholder = placeholder;
      return placeholder;
    }

    const container = document.createElement('div');
    container.className = 'mapboxgl-ctrl mapboxgl-ctrl-group';
    container.appendChild(button);
    return container;
  }

  onRemove(): void {
    this.button?.remove();
    this.placeholder?.remove();
    this.button = null;
    this.placeholder = null;
    this.map = null;
    this.enabled = false;
  }

  private setLabel(button: HTMLButtonElement, enabled: boolean) {
    const label = enabled ? 'Hide 3D' : 'Show 3D';
    button.title = label;
    button.setAttribute('aria-label', label);
    button.setAttribute('aria-pressed', String(enabled));
  }

  private toggle = () => {
    const map = this.map;
    const button = this.button;
    if (!map || !button) return;
    this.enabled = !this.enabled;
    this.setLabel(button, this.enabled);
    if (this.enabled) {
      showMap3d(map, pitchDuration());
      return;
    }
    hideMap3d(map, pitchDuration());
  };
}
